import { CouponIssueErrorCodes } from '@diritto/common/coupon/constants/coupon-issue.error-codes.constants';
import { CouponCatalogDto } from '@diritto/common/coupon/dtos/coupon-catalog.dto';
import { EventProgressType } from '@diritto/common/event/constants';
import { EventDto } from '@diritto/common/event/dtos/event.dto';
import { EventParticipateErrorCodes } from '@diritto/common/event/event-participate.error-codes.constants';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { AppInstallLink, AppOsType } from '@diritto/common/system/constants';
import styled from '@emotion/styled';
import { AxiosError } from 'axios';
import { ButtonSize } from 'constants/button';
import { useAuthState } from 'contexts/AuthProvider';
import { useRouter } from 'next/navigation';
import { useGetIssuedCouponCatalogQuery } from 'queries/couponCatalog/useGetIssuedCouponCatalogQuery';
import { useGetEventParticipatedQuery } from 'queries/events/useGetEventParticipatedQuery';
import { useParticipateEventMutation } from 'queries/events/useParticipateEventMutation';
import { useCallback, useMemo } from 'react';
import { Button } from 'ui-components/common/Button';
import { Loading } from 'ui-components/common/Loading';
import { useClient } from 'ui-hooks/useClient';
import { useCopyText } from 'ui-hooks/useCopyText';
import { useExternalLink } from 'ui-hooks/useExternalLink';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestMarketingAgreement } from 'ui-hooks/useRequestMarketingAgreement';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showAlertModal, showToast } from 'utils/modal';

interface EventRewardProps {
  couponCatalog: CouponCatalogDto;
  event: EventDto;
}

export const EventRewardRaffle = ({ couponCatalog, event }: EventRewardProps) => {
  const { isLoggedIn } = useAuthState();
  const client = useClient();
  const requestSign = useRequestSign();
  const copyText = useCopyText();
  const openLinkToExternal = useExternalLink();
  const requestCertificate = useRequestCertificate();
  const requestMarketingAgreement = useRequestMarketingAgreement();
  const router = useRouter();
  const appInstallUrl = client.isAosWeb ? AppInstallLink[AppOsType.Android] : AppInstallLink[AppOsType.Ios];

  const { data: issuedCouponCatalogData, isFetched: isIssuedCouponCatalogDataFetched } = useGetIssuedCouponCatalogQuery(
    couponCatalog.id,
    isLoggedIn,
  );
  const couponSerialNumber = useMemo(() => {
    if (!issuedCouponCatalogData || issuedCouponCatalogData.coupons.length === 0) {
      return undefined;
    }

    const [coupon] = issuedCouponCatalogData.coupons;
    return coupon.rewardInfo.serialNumber;
  }, [issuedCouponCatalogData]);

  const { data: eventParticipatedData, isFetched: isEventParticipatedDataFetched } = useGetEventParticipatedQuery(
    event.id,
    isLoggedIn,
  );
  const eventParticipated = eventParticipatedData?.participated;

  const handleClickCopySerialNumber = useCallback(async () => {
    try {
      await copyText(couponSerialNumber || '');
      showToast('쿠폰 코드가 복사되었어요.');
    } catch {
      showToast('쿠폰 코드 복사에 실패했어요.');
    }
  }, [copyText, couponSerialNumber]);

  const { mutate: participateMutate } = useParticipateEventMutation({
    onSuccess: () => {
      showAlertModal({
        title: '이벤트에 참여 완료했어요.',
        description: `밤 11시에 알림센터와 이벤트 페이지에서 추첨 결과를 확인해 보세요.`,
      });
    },
    onError: e => {
      const errorCode = (e as AxiosError<{ code: CouponIssueErrorCodes | EventParticipateErrorCodes }>).response?.data
        ?.code;

      switch (errorCode) {
        case EventParticipateErrorCodes.ExpiredParticipateDate:
          showAlertModal({
            title: '종료된 이벤트예요.',
            description: '아쉽지만 다음에 참여 부탁드려요!',
          });
          return;
        case EventParticipateErrorCodes.AlreadyParticipateEvent:
          showAlertModal({
            title: '이미 참여하셨어요.',
            description: '잠시만 기다려주세요!',
          });
          return;

        case CouponIssueErrorCodes.ExpiredIssueDate:
          showAlertModal({
            title: '종료된 이벤트예요.',
            description: '아쉽지만 다음에 참여 부탁드려요!',
          });
          return;
        case CouponIssueErrorCodes.InsufficientIssueRequirements:
          showAlertModal({
            title: '조건을 달성해 주세요.',
            description: '이벤트 조건 달성 후에 혜택을 받을 수 있어요.',
          });
          return;
        case CouponIssueErrorCodes.InsufficientIssueRequirementsReply:
          showAlertModal({
            title: '조건을 달성해 주세요.',
            description: '댓글 작성 조건 달성 후에 후에 다시 참여해주세요.',
          });
          return;
        case CouponIssueErrorCodes.InsufficientIssueRequirementsEpisodeRead:
          showAlertModal({
            title: '조건을 달성해 주세요.',
            description: '작품 읽기 조건 달성 후에 다시 참여해주세요.',
          });
          return;
        case CouponIssueErrorCodes.NeedMarketingAgreement:
          showAlertModal(
            {
              title: '리워드를 받으려면 이벤트 / 추천 알림이 ON되어 있어야 해요.',
              description: '리디포인트 등 디리토에서 리워드를 받으려면 이벤트 / 추천 알림이 ON되어 있어야 해요',
            },
            {
              cancelText: '닫기',
              confirmText: '이벤트 / 추천 알림 설정하러 가기',
              onConfirm: requestMarketingAgreement,
            },
          );
          return;
        case CouponIssueErrorCodes.NeedUserCertification:
          showAlertModal(
            {
              title: '혜택을 받으려면 본인인증이 필요해요.',
              description: '본인인증 후 혜택을 받을 수 있어요.',
            },
            {
              cancelText: '닫기',
              confirmText: '본인인증하러 가기',
              onConfirm: requestCertificate,
            },
          );
          return;

        case CouponIssueErrorCodes.MaximumIssueExceededPerUser:
          showAlertModal({
            title: '이미 혜택을 받았어요.',
            description: '다른 계정을 이용하여 이미 혜택을 수령한 경우 중복으로 혜택을 받을 수 없어요.',
          });
          return;

        case CouponIssueErrorCodes.AlreadyIssuePreviousCouponCatalog:
          showAlertModal({
            title: '다른 이벤트를 통해서 이미 혜택을 받았어요.',
            description: '다른 이벤트를 이용하여 이미 혜택을 수령한 경우 중복으로 혜택을 받을 수 없어요.',
          });
          return;

        case CouponIssueErrorCodes.MaximumIssueExceeded:
          showAlertModal({
            title: '모든 쿠폰이 소진되었어요.',
            description: '준비한 모든 쿠폰이 소진되었어요.',
          });
          return;
        default:
          showAlertModal(
            {
              title: '이벤트 참여에 문제가 있어요.',
              description: '고객센터로 문의해주세요.',
            },
            {
              cancelText: '닫기',
              confirmText: '고객센터 가기',
              onConfirm: () => router.push('/help'),
            },
          );
      }
    },
  });

  const handleClickReward = useCallback(() => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }

    if (event.eventProgressType !== EventProgressType.Proceeding) {
      showAlertModal('종료된 이벤트예요.');
      return;
    }

    if (event.isAppOnly && !client.isApp) {
      showAlertModal(
        {
          title: '앱에서 접속하면 혜택을 받을 수 있어요.',
          description: '앱을 설치해서 혜택을 받아보세요!',
        },
        {
          cancelText: '닫기',
          confirmText: '앱 다운로드하기',
          onConfirm: () => openLinkToExternal(appInstallUrl),
        },
      );
      return;
    }

    participateMutate(event.id);
  }, [appInstallUrl, client.isApp, event, isLoggedIn, openLinkToExternal, participateMutate, requestSign]);

  if (isLoggedIn && (!isIssuedCouponCatalogDataFetched || !isEventParticipatedDataFetched)) {
    return (
      <StylesContainer>
        <Loading />
      </StylesContainer>
    );
  }

  if (couponSerialNumber) {
    return (
      <StylesContainer>
        <StylesSerialNumberWrapper>
          <StylesSerialNumber>{couponSerialNumber}</StylesSerialNumber>
          <Button label="복사" onClick={handleClickCopySerialNumber} />
        </StylesSerialNumberWrapper>
      </StylesContainer>
    );
  }

  if (eventParticipated) {
    return (
      <StylesContainer>
        <StylesButton disabled size={ButtonSize.Large} buttonTypography={Typography.XSmall} label="참여 완료" />
      </StylesContainer>
    );
  }

  return (
    <StylesContainer>
      <StylesButton
        size={ButtonSize.Large}
        buttonTypography={Typography.XSmall}
        label="참여하기"
        onClick={handleClickReward}
      />
    </StylesContainer>
  );
};

const StylesContainer = styled.section`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StylesSerialNumberWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 48px;
  margin-top: 24px;
`;

const StylesSerialNumber = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  border: 1px solid ${Color.Gray400};
  border-radius: 4px;
  margin-right: 8px;
  ${Typography.XXSmall}
`;

const StylesButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
`;
